// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { advisoryApi } from '@shared/data-access/api/brokerage/advisory-api.v2';
import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { type LoaderStatus } from '../components/loader/loader';
import {
  selectAccountCurrency,
  selectUserId,
} from '@shared/data-access/slices/user';
import type { Client } from '@shared/data-access/types/brokerage-types';

export const CLIENTS_PAGE_SIZE = 50;

export interface ClientRow {
  id: number;
  email: string;
  name: string;
  status?: Client['status'];
  citec_user_id: number;
  risk_profile?: string | null;
  volume: number;
}

export interface UseClientsParams {
  page: number;
  search?: string;
}

export const useClients = ({ page, search }: UseClientsParams) => {
  const currency = useSelector(selectAccountCurrency);
  const userId = useSelector(selectUserId);

  const clientsQuery = advisoryApi.useGetAdvisoryClientsQuery({
    page,
    page_size: CLIENTS_PAGE_SIZE,
    search: search || undefined,
    currency,
  });

  const clientsData = useMemo(() => {
    const clientsPage = clientsQuery.data;

    if (!clientsPage) {
      return [];
    }

    return clientsPage.data.map<ClientRow>(client => ({
      id: client.id,
      email: client.email,
      name: client.name || client.email.split('@')[0],
      status: client.status || undefined,
      citec_user_id: client.citec_user_id,
      risk_profile: client.risk_profile,
      volume: client.total_volume,
    }));
  }, [clientsQuery.data]);

  const status = useMemo<LoaderStatus>(() => {
    const isLoading = clientsQuery.isLoading;
    const isError = clientsQuery.isError;
    const isFetching = clientsQuery.isFetching;

    return isLoading
      ? 'pending'
      : isError && !isFetching
      ? 'rejected'
      : isFetching
      ? 'fetching'
      : 'fulfilled';
  }, [clientsQuery.isError, clientsQuery.isFetching, clientsQuery.isLoading]);

  const hasData = useMemo(() => {
    return (clientsQuery.data?.data.length ?? 0) > 0;
  }, [clientsQuery.data?.data.length]);

  const onRefresh = useCallback(() => {
    clientsQuery.refetch();
  }, [clientsQuery]);

  return {
    clientsPage: clientsQuery.data,
    clientsData,
    clientsQuery,
    status,
    hasData,
    onRefresh,
    userId,
  };
};
